// Read-only view of marketingPlan and marketingPlanList tool results.
//
// Two payloads are accepted. The current chat tool returns `data.card`
// plus `data.text` (text is model context and is not shown). getPlan now
// returns the compact summary from backend #660: `card` is absent, and
// the result has `title`, `status`, `revision`, `weeklyTotal`, `currency`,
// `phase2Budget`, `margin`, `lines` counts, `sections`, `markdown`, and
// `text`. `text` stays hidden. Missing fields are skipped. Account ids,
// tokens, and feed keys are never read.

export type PlanTestView = {
  label: string;
  budget: string | null;
  blockers: string[];
};

export type PlanView = {
  /** Request id only. Never rendered. */
  id: string | null;
  title: string;
  status: string | null;
  revision: string | null;
  weeklyBudget: string | null;
  phase2Budget: string | null;
  margin: string | null;
  summary: string | null;
  counts: string | null;
  blockers: string[];
  markdown: string | null;
  tests: PlanTestView[];
};

export type MarketingPlansView = {
  kind: 'marketingPlan' | 'marketingPlanList';
  summary: string | null;
  counts: string | null;
  markdown: string | null;
  blockers: string[];
  plans: PlanView[];
};

const LIST_KEYS = ['plans', 'items', 'cards'] as const;
const COUNT_KEYS = ['plans', 'tests', 'blockers', 'ads', 'campaigns', 'lines', 'phases'] as const;
const COUNT_LABEL: Record<(typeof COUNT_KEYS)[number], [string, string]> = {
  plans: ['plan', 'plans'],
  tests: ['test', 'tests'],
  blockers: ['blocker', 'blockers'],
  ads: ['ad', 'ads'],
  campaigns: ['campaign', 'campaigns'],
  lines: ['line', 'lines'],
  phases: ['phase', 'phases']
};

type Rec = Record<string, unknown>;

function isRec(value: unknown): value is Rec {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function str(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function num(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function records(value: unknown): Rec[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRec);
}

function unsafe(text: string): boolean {
  if (/bearer\s+\S{8,}/i.test(text)) return true;
  if (/EAA[A-Za-z0-9]{10,}/.test(text)) return true;
  if (/access[_-]?token/i.test(text)) return true;
  if (/\b(secret|password|api[_-]?key)\b\s*[:=]\s*\S+/i.test(text)) return true;
  if (/feed[_-]?key\s*[:=]\s*\S+/i.test(text)) return true;
  if (/act_\d{4,}/.test(text)) return true;
  return false;
}

function safeText(value: unknown, limit: number): string | null {
  const text = str(value);
  if (!text || text.length > limit || unsafe(text)) return null;
  return text;
}

function currencyCode(value: unknown): string | null {
  const text = str(value);
  if (!text || !/^[A-Za-z]{3}$/.test(text)) return null;
  return text.toUpperCase();
}

function formatCount(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
}

function formatPlain(value: number): string {
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
}

function formatMoney(value: number, currency: string | null): string {
  if (currency) {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);
    } catch {
      return `${formatPlain(value)} ${currency}`;
    }
  }
  return formatPlain(value);
}

function dataOf(payload: unknown): Rec {
  if (!isRec(payload)) return {};
  if (isRec(payload.data)) return payload.data;
  return payload;
}

function kindOf(payload: unknown): 'marketingPlan' | 'marketingPlanList' {
  const root = isRec(payload) ? payload : null;
  const data = root && isRec(root.data) ? root.data : null;
  const kind = str(root?.kind) ?? (data ? str(data.kind) : null);
  return kind === 'marketingPlanList' ? 'marketingPlanList' : 'marketingPlan';
}

function layers(node: Rec): Rec[] {
  const out = [node];
  if (isRec(node.summary)) out.push(node.summary);
  if (isRec(node.plan)) out.push(node.plan);
  return out;
}

function pick(node: Rec, keys: string[]): unknown {
  for (const layer of layers(node)) {
    for (const key of keys) {
      const value = layer[key];
      if (value == null) continue;
      if (typeof value === 'string' && !value.trim()) continue;
      return value;
    }
  }
  return null;
}

function renderBudget(value: unknown, currency: string | null): string | null {
  if (isRec(value)) {
    const nested = currencyCode(value.currency) ?? currency;
    const amount = num(value.amount ?? value.weekly ?? value.weeklyBudget ?? value.usd ?? value.value);
    if (amount == null) return null;
    return formatMoney(amount, nested);
  }
  if (typeof value === 'string') {
    const text = value.trim();
    if (!text || unsafe(text)) return null;
    const amount = num(text);
    if (amount != null) return formatMoney(amount, currency);
    return text.length <= 40 ? text : null;
  }
  const amount = num(value);
  if (amount == null) return null;
  return formatMoney(amount, currency);
}

function budgetOf(node: Rec, keys: string[], fallback: string | null = null): string | null {
  const currency = currencyCode(pick(node, ['currency'])) ?? fallback;
  for (const key of keys) {
    const rendered = renderBudget(pick(node, [key]), currency);
    if (rendered) return rendered;
  }
  return null;
}

function blockerText(value: unknown): string | null {
  if (isRec(value)) {
    return safeText(value.message ?? value.reason ?? value.text ?? value.detail ?? value.label ?? value.name, 240);
  }
  return safeText(value, 240);
}

function blockersFrom(value: unknown): string[] {
  if (value == null) return [];
  const items = Array.isArray(value) ? value : [value];
  const out: string[] = [];
  for (const item of items) {
    const text = blockerText(item);
    if (text && !out.includes(text)) out.push(text);
  }
  return out;
}

function blockersOf(node: Rec): string[] {
  return blockersFrom(pick(node, ['blockers', 'blocker']));
}

const BUDGET_KEYS = ['weeklyTotal', 'weeklyBudget', 'weeklyBudgetUsd', 'budgetWeekly', 'budget'];

function lineCountLine(value: unknown): string | null {
  if (!isRec(value)) return null;
  const parts: string[] = [];
  for (const key of ['reuse', 'create', 'unbound'] as const) {
    const n = num(value[key]);
    if (n == null) continue;
    parts.push(`${formatCount(n)} ${key}`);
  }
  return parts.length ? parts.join(' · ') : null;
}

function planCounts(node: Rec): string | null {
  const counts = countLine(pick(node, ['counts']));
  const lines = lineCountLine(pick(node, ['lines']));
  return [counts, lines].filter(Boolean).join(' · ') || null;
}

function countLine(value: unknown): string | null {
  if (!isRec(value)) return null;
  const parts: string[] = [];
  for (const key of COUNT_KEYS) {
    const n = num(value[key]);
    if (n == null) continue;
    const [one, many] = COUNT_LABEL[key];
    parts.push(`${formatCount(n)} ${n === 1 ? one : many}`);
  }
  return parts.length ? parts.join(' · ') : null;
}

function requestId(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  const text = str(value);
  if (!text || text.length > 80 || unsafe(text)) return null;
  return text;
}

function shortId(value: unknown): string | null {
  const text = safeText(value, 12);
  if (!text || /^[a-f0-9]{24}$/i.test(text)) return null;
  return text;
}

function readTests(node: Rec): PlanTestView[] {
  const rows = records(pick(node, ['tests']));
  const planCurrency = currencyCode(pick(node, ['currency']));
  const tests: PlanTestView[] = [];
  rows.forEach((row, index) => {
    const name = safeText(row.name ?? row.title ?? row.label, 160);
    const id = shortId(row.id ?? row.testId);
    const budget = budgetOf(row, ['budget', 'weeklyBudget', 'weeklyBudgetUsd', 'amount'], planCurrency);
    const blockers = blockersOf(row);
    if (!name && !id && !budget && blockers.length === 0) return;
    tests.push({
      label: name ?? (id ? `Test ${id}` : `Test ${index + 1}`),
      budget,
      blockers
    });
  });
  return tests;
}

function marginOf(node: Rec): string | null {
  const value = pick(node, ['margin']);
  const text = safeText(value, 40);
  if (text && num(text) == null) return text;
  const amount = num(value);
  if (amount == null) return null;
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(amount);
}

function readRevision(node: Rec): string | null {
  const value = pick(node, ['revision', 'revisionNumber', 'rev']);
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (isRec(value)) {
    const inner = value.number ?? value.revision ?? value.label ?? value.name;
    if (typeof inner === 'number' && Number.isFinite(inner)) return String(inner);
    return safeText(inner, 40);
  }
  return safeText(value, 40);
}

function readSummary(node: Rec): string | null {
  for (const layer of layers(node)) {
    const summary = layer.summary;
    if (typeof summary === 'string') {
      const text = safeText(summary, 600);
      if (text) return text;
    }
  }
  return null;
}

export function readPlan(node: Rec): PlanView {
  return {
    id: requestId(pick(node, ['planId', 'id'])),
    title: safeText(pick(node, ['title', 'name']), 160) ?? 'Marketing plan',
    status: safeText(pick(node, ['status']), 40),
    revision: readRevision(node),
    weeklyBudget: budgetOf(node, BUDGET_KEYS),
    phase2Budget: budgetOf(node, ['phase2Budget']),
    margin: marginOf(node),
    summary: readSummary(node),
    counts: planCounts(node),
    blockers: blockersOf(node),
    markdown: safeText(pick(node, ['markdown']), 4000),
    tests: readTests(node)
  };
}

export function planHasDetails(plan: PlanView): boolean {
  return Boolean(
    (plan.title && plan.title !== 'Marketing plan')
    || plan.status
    || plan.revision
    || plan.weeklyBudget
    || plan.phase2Budget
    || plan.margin
    || plan.summary
    || plan.counts
    || plan.markdown
    || plan.blockers.length
    || plan.tests.length
  );
}

function displayNode(data: Rec): Rec {
  const card = isRec(data.card) ? data.card : null;
  if (!card) return data;
  return {
    ...data,
    ...card,
    summary: card.summary ?? data.summary,
    tests: records(card.tests).length ? card.tests : data.tests,
    counts: card.counts ?? data.counts,
    blockers: card.blockers ?? data.blockers,
    markdown: card.markdown ?? data.markdown
  };
}

function findListedPlans(data: Rec): Rec[] | null {
  const roots = [data, isRec(data.card) ? data.card : null, isRec(data.summary) ? data.summary : null];
  for (const root of roots) {
    if (!root) continue;
    for (const key of LIST_KEYS) {
      const rows = records(root[key]);
      if (rows.length) return rows;
    }
  }
  return null;
}

function hasOwnPlanFields(node: Rec): boolean {
  return Boolean(
    safeText(pick(node, ['title', 'name']), 160)
    || safeText(pick(node, ['status']), 40)
    || readRevision(node)
    || budgetOf(node, BUDGET_KEYS)
    || records(pick(node, ['tests'])).length
    || safeText(pick(node, ['markdown']), 1200)
  );
}

function headerFrom(data: Rec, listed: boolean): Pick<MarketingPlansView, 'summary' | 'counts' | 'markdown' | 'blockers'> {
  if (!listed) {
    return { summary: null, counts: null, markdown: null, blockers: [] };
  }
  const card = isRec(data.card) ? data.card : null;
  const summary = typeof data.summary === 'string'
    ? safeText(data.summary, 600)
    : (card && typeof card.summary === 'string' ? safeText(card.summary, 600) : null);
  return {
    summary,
    counts: countLine(data.counts ?? card?.counts),
    markdown: safeText(data.markdown ?? card?.markdown, 1200),
    blockers: blockersFrom(data.blockers ?? card?.blockers)
  };
}

export function readMarketingPlans(payload: unknown): MarketingPlansView {
  const kind = kindOf(payload);
  const data = dataOf(payload);
  const listed = findListedPlans(data);
  const node = displayNode(data);
  if (kind === 'marketingPlanList' || (listed != null && !hasOwnPlanFields(node))) {
    const rows = listed ?? (hasOwnPlanFields(node) ? [node] : []);
    return {
      kind,
      ...headerFrom(data, listed != null),
      plans: rows.map((row) => readPlan(row))
    };
  }
  return {
    kind,
    summary: null,
    counts: null,
    markdown: null,
    blockers: [],
    plans: [readPlan(node)]
  };
}
